import { useEffect, useRef } from 'react';

export interface InfiniteScrollSentinelOptions {
  enabled: boolean;
  rootMargin?: string;
}

/**
 * Returns a ref for a sentinel element placed at the end of an infinite
 * scroll list. Calls `onIntersect` whenever the sentinel enters the
 * viewport while `enabled` is true, so the caller can load the next page.
 */
export function useInfiniteScrollSentinel<T extends Element>(
  onIntersect: () => void,
  { enabled, rootMargin = '600px' }: InfiniteScrollSentinelOptions,
) {
  const sentinelRef = useRef<T | null>(null);
  const onIntersectRef = useRef(onIntersect);
  onIntersectRef.current = onIntersect;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !enabled || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onIntersectRef.current();
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, rootMargin]);

  return sentinelRef;
}
